/**
 * Границы между панелями экрана черновика.
 *
 * Экран собирается из трёх кусков, которые пишутся и правятся независимо (исходник →
 * превью → решение, docs/ui-design.md §5.2). Чтобы они не начали читать состояние друг
 * друга, весь их контакт описан здесь и только здесь: пропсы внутрь, коллбэк «данные
 * изменились» наружу. Никакого общего стора нет намеренно — его первое же появление
 * превратит три панели в один компонент на 900 строк.
 */

import type { DraftContent } from '@smm/contracts';
import type { ReactNode } from 'react';
import type { BacklogItem, MediaRef, RenderModel } from '../api/types.ts';
import type { DocumentUpload } from './transport/useDocumentUpload.ts';
import type { VisualUpload } from './visual/useVisualUpload.ts';

/** Три уровня проверок — docs/phase-1/05-ui.md §4.2. */
export type UiIssueLevel = 'blocking' | 'warning' | 'info';

export interface UiIssue {
  readonly level: UiIssueLevel;
  readonly code: string;
  /** Готовая фраза по-русски: экран её показывает, не переводя. */
  readonly message: string;
}

export const hasBlocking = (issues: readonly UiIssue[]): boolean =>
  issues.some((i) => i.level === 'blocking');

export interface BacklogAsideProps {
  readonly brand: string;
  readonly items: readonly BacklogItem[] | undefined;
  readonly loading: boolean;
  readonly error: Error | undefined;
  readonly selectedDraftId: string | null;
}

export interface DraftPanelProps {
  readonly brand: string;
  readonly draftId: string;
  /** null — текущая версия черновика. */
  readonly versionNo: number | null;
  /** Вызывается после любого действия, изменившего состояние на сервере. */
  readonly onChanged: () => void;
  /**
   * Черновик заказан расписанием — шапка рисует пометку «авто». Приходит из строки бэклога
   * (`BacklogItem.auto`): модель черновика этого признака не несёт, а бэклог уже загружен
   * владельцем экрана. Нет строки (пост за пределами выборки) — пометки нет.
   */
  readonly auto?: boolean;
}

/**
 * Набранный, ещё не сохранённый контент. Живёт отдельным типом, потому что это НЕ
 * `CanonicalDraft`: бренд каноничности ставит только `canonicalize()`, а здесь текст ровно
 * такой, каким его набрал человек.
 */
export type TypedContent = Pick<DraftContent, 'text' | 'entities'>;

export interface PreviewPanelProps {
  readonly model: RenderModel;
  /**
   * Если не `null` — превью рисует НАБРАННЫЙ текст и помечает себя как несохранённую правку.
   * Решение Ильи 22.08 — «разметка в исходнике, живое превью справа» — это ровно оно.
   *
   * Валидация и лимиты при этом остаются на `model.canonical`: ключ выпускается под
   * СОХРАНЁННУЮ версию, и подменять то, что уходит под подпись, показанным превью нельзя.
   */
  readonly typed?: TypedContent | null;
  /**
   * Картинка-КАНДИДАТ: она ещё не в версии и не в `content_hash`. Не `null` — превью рисует
   * пост так, как он выглядел бы с ней, и помечает себя строкой «показано предложение»,
   * ровно как это сделано для набранного текста.
   *
   * Валидация, лимиты и ключ при этом по-прежнему считаются по `model.canonical`: подменять
   * показанным превью то, что уходит под подпись, запрещено (см. `typed` выше). Без этого
   * пропа человек решает «принять или отклонить», не увидев картинку в посте, — то есть
   * решает вслепую.
   */
  readonly proposed?: DraftContent['media'] | null;
  /** Адрес байтов кандидата; непусто ⟺ `proposed !== null`. */
  readonly proposedRef?: MediaRef | null;
  /**
   * Политика бренда «все посты — расширенным сообщением» СЕЙЧАС (docs/release-2026-09-19.md
   * §9.3); `undefined` — неизвестна. Нужна ровно для несохранённой правки: она сохранится по
   * этой политике, и её транспорт выводится по ней (сохранённая версия несёт свой `method`).
   */
  readonly richAlways?: boolean | undefined;
  /** Действие в плашке карточки — «Перевести в расширенное» (владеет им экран, не превью). */
  readonly cardAction?: ReactNode;
}

export interface SourceEditorPanelProps {
  readonly model: RenderModel;
  readonly onChanged: () => void;
  /** Сообщает наружу набранный контент: `null` — правки нет, превью показывает версию. */
  readonly onTypedChange?: ((typed: TypedContent | null) => void) | undefined;
  /**
   * Политика бренда СЕЙЧАС; `undefined` — неизвестна (тогда — `TRANSPORT_RICH_ALWAYS_DEFAULT`,
   * как у бэкенда без строки). Предсохранная заморозка зовётся с ней: иначе редактор проверял
   * бы правку не той формой, которую ей выберет сервер.
   */
  readonly richAlways?: boolean | undefined;
}

/**
 * Полоса «Файл» — PDF внизу поста (docs/release-2026-09-19.md §9.2), пятый орган экрана.
 * Состояние загрузки живёт в хуке ЭКРАНА по той же причине, что у полосы визуала: смена
 * режима экрана пересоздаёт полосу, а загрузка в 20 МиБ обязана её пережить.
 */
export interface DocumentStripProps {
  readonly model: RenderModel;
  readonly upload: DocumentUpload;
}

/**
 * Полоса «Мнение автора» (AGI Boardroom, build spec 2026-11-02 §4) — орган экрана под полосой
 * файла, во всех трёх режимах. Рисуется только у бренда, которому список брендов разрешил мнение
 * (`brandAuthorOpinion`); бренд — `model.brand`, как у полосы решения. Состояния загрузки у неё
 * нет, поэтому хозяина в экране ей не нужно: всё, что она знает, — модель версии.
 */
export interface OpinionStripProps {
  readonly model: RenderModel;
  /**
   * Мнение ушло на переделку. Версии это не родило — её родит писатель, — поэтому экран
   * перечитывается без сброса набранной правки.
   */
  readonly onChanged: () => void;
}

/**
 * Бренда в пропах нет намеренно (2026-10-25, изоляция брендов): полоса решения берёт его из
 * `model.brand`. Бренд адреса страницы может быть чужим черновику — до редиректа экрана
 * черновика или в тестах, — и ни один запрос, выпускающий ключ, не должен его увидеть.
 */
export interface DecisionPanelProps {
  readonly model: RenderModel;
  readonly issues: readonly UiIssue[];
  readonly onChanged: () => void;
}

/**
 * Контакт полосы визуала с экраном черновика. Полоса — четвёртый орган этого экрана, и её
 * границу описывает этот файл по той же причине, что и три остальных: состояние загрузки
 * живёт в хуке экрана (зона сброса — вся колонка поста, а не полоса), а `showProposal`
 * читают ДВОЕ — полоса и превью, — поэтому владелец у него один, и это экран.
 */
export interface VisualStripProps {
  readonly model: RenderModel;
  readonly upload: VisualUpload;
  /** Показывать ли кандидата в пузыре превью. Осмыслен только в состоянии «предложено». */
  readonly showProposal: boolean;
  readonly onShowProposal: (on: boolean) => void;
  /** Вызывается после любого действия, изменившего состояние на сервере. */
  readonly onChanged: () => void;
}
